import { useState } from "react";
import { Loader2, Sparkles, Zap } from "lucide-react";
import { clsx } from "clsx";
import { classifyTasks, suggestFirstMove } from "../lib/ai-pwa";
import { classificationKey, getCached, setCached } from "../lib/classify-cache";
import type { ClassifyResult, FirstMove, Task } from "../lib/tauri";

interface Props {
  activeTasks: Task[];
}

type Column = { key: "today" | "thisWeek" | "later"; label: string; color: string };

const COLUMNS: Column[] = [
  { key: "today", label: "今日やるべき", color: "border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-400/30 dark:bg-rose-400/10 dark:text-rose-100" },
  { key: "thisWeek", label: "今週中", color: "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-100" },
  { key: "later", label: "後回し", color: "border-border bg-bg text-muted dark:border-zinc-600 dark:bg-zinc-800/60 dark:text-zinc-300" },
];

function reasonFor(task: Task): string {
  const labels: string[] = [];
  if (task.priority === "high") labels.push("優先度高");
  if (task.dueDate) {
    const due = new Date(task.dueDate + "T00:00:00");
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diff = Math.round((due.getTime() - today.getTime()) / 86400000);
    if (diff < 0) labels.push("期限超過");
    else if (diff === 0) labels.push("期日が今日");
    else if (diff <= 2) labels.push("期日が近い");
    else if (diff >= 7) labels.push("先の予定");
  } else {
    labels.push("期日なし");
  }
  return labels.join("・") || "—";
}

function friendlyError(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err);
  if (msg.includes("Ollama unreachable") || msg.includes("Connection refused") || msg.includes("ECONNREFUSED")) {
    return "AIサーバーに接続できません。しばらく待ってからもう一度お試しください。";
  }
  return `AI分類エラー: ${msg.slice(0, 120)}`;
}

export function AiClassifyPanel({ activeTasks }: Props) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ClassifyResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [firstMove, setFirstMove] = useState<FirstMove | null>(null);
  const [firstMoveLoading, setFirstMoveLoading] = useState(false);

  // 先読み(プリフェッチ)は App 最上位で常時駆動される(ビュー非依存)。
  // ここでは押下時にキャッシュを参照し、ヒットなら即表示／ミスなら通常実行する。

  const handleClassify = async () => {
    const key = classificationKey(activeTasks);

    // (1) キャッシュヒット → 即表示(ローディングなし=体感ゼロ秒)
    const cached = getCached(key);
    if (cached) {
      setError(null);
      setResult(cached.classify);
      setFirstMove(cached.firstMove);
      setFirstMoveLoading(false);
      setLoading(false);
      return;
    }

    // (2) キャッシュミス → 現状の段階表示でフォールバック(分類先→一手後追い)
    setLoading(true);
    setError(null);
    setResult(null);
    setFirstMove(null);
    setFirstMoveLoading(false);
    try {
      // (a) 分類だけを先に取得して即表示
      const res = await classifyTasks(activeTasks);
      setResult(res);
      setLoading(false);

      // (b) 分類が返ったら「今日の一手」を追いかけて取得（独自ローディング）
      setFirstMoveLoading(true);
      let fm: FirstMove = { taskId: null, message: "" };
      try {
        fm = await suggestFirstMove(activeTasks);
        setFirstMove(fm);
      } catch {
        // 一手の取得失敗は分類結果を壊さない（カードを出さないだけ）
        setFirstMove(null);
      } finally {
        setFirstMoveLoading(false);
      }

      // 結果をキャッシュにも保存(次回押下/同タスクで即表示)
      setCached(key, { classify: res, firstMove: fm });
    } catch (err) {
      setError(friendlyError(err));
      setLoading(false);
    }
  };

  // id → Task のマップ
  const taskMap = new Map(activeTasks.map((t) => [t.id, t]));

  return (
    <section className="command-card p-4">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <div>
          <p className="command-muted text-sm font-semibold">AI</p>
          <h2 className="mt-1 text-xl font-bold">タスク分類</h2>
        </div>
        <Sparkles className="h-5 w-5 text-brand-600 dark:text-indigo-200" />
      </div>

      {/* Button */}
      <button
        type="button"
        onClick={() => void handleClassify()}
        disabled={loading || activeTasks.length === 0}
        className={clsx(
          "mb-4 flex w-full items-center justify-center gap-2 rounded-md px-4 py-2.5 text-sm font-semibold transition active:scale-95",
          "bg-gradient-to-r from-indigo-500 to-violet-500 text-white shadow-md",
          (loading || activeTasks.length === 0) && "cursor-not-allowed opacity-50"
        )}
      >
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Sparkles className="h-4 w-4" />
        )}
        {loading ? "AI 分類中…" : "AIで整理"}
      </button>

      {activeTasks.length === 0 && !loading && !result && (
        <p className="command-dim text-center text-xs">未完了タスクがありません</p>
      )}

      {/* Error */}
      {error && (
        <p className="rounded-md border border-rose-400/30 bg-rose-400/10 px-3 py-2 text-xs text-rose-200">
          {error}
        </p>
      )}

      {/* First Move card — 分類より遅れて表示。先にローディング、来たら一手 */}
      {result && firstMoveLoading && (
        <div className="mb-4 flex items-center gap-2 rounded-md border border-brand-200 bg-brand-50 p-3 dark:border-indigo-400/30 dark:bg-indigo-500/10">
          <Loader2 className="h-3.5 w-3.5 animate-spin text-brand-600 dark:text-indigo-300" />
          <span className="text-xs font-medium text-brand-700 dark:text-indigo-200">今日の一手を考え中…</span>
        </div>
      )}
      {result && !firstMoveLoading && firstMove?.taskId && (
        <div className="mb-4 rounded-md border border-brand-200 bg-brand-50 p-3 dark:border-indigo-400/30 dark:bg-indigo-500/10">
          <div className="mb-1 flex items-center gap-1.5">
            <Zap className="h-3.5 w-3.5 text-brand-600 dark:text-indigo-300" />
            <span className="text-xs font-bold text-brand-700 dark:text-indigo-200">今日の一手</span>
          </div>
          <p className="text-sm font-semibold leading-snug text-text dark:text-white">
            {firstMove.message}
          </p>
          {(() => {
            const t = taskMap.get(firstMove.taskId!);
            return t ? (
              <p className="mt-1 truncate text-xs text-brand-600 dark:text-indigo-300/80">▶ {t.title}</p>
            ) : null;
          })()}
        </div>
      )}

      {/* Result columns */}
      {result && (
        <div className="grid gap-3 sm:grid-cols-3">
          {COLUMNS.map(({ key, label, color }) => {
            const ids: string[] = result[key];
            const tasks = ids.flatMap((id) => {
              const t = taskMap.get(id);
              return t ? [t] : [];
            });
            return (
              <div key={key} className={clsx("rounded-md border p-3", color)}>
                <div className="mb-2 flex items-center gap-1.5">
                  <p className="text-xs font-bold uppercase tracking-wide opacity-70">{label}</p>
                  {tasks.length > 0 && (
                    <span className="rounded-full bg-white/20 px-1.5 py-0.5 text-[10px] font-bold leading-none">
                      {tasks.length}
                    </span>
                  )}
                </div>
                {tasks.length === 0 ? (
                  <p className="text-xs opacity-50">なし</p>
                ) : (
                  <ul className="space-y-2">
                    {tasks.map((t) => (
                      <li key={t.id}>
                        <p className="truncate text-xs font-medium">{t.title}</p>
                        <p className="text-[10px] opacity-60 mt-0.5">{reasonFor(t)}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
